import { useEffect, useRef, useState } from "react";
import { Loader2, UploadCloud } from "lucide-react";
import { api, formatApiErrorDetail, mediaUrl } from "../lib/api";
import { uploadMedia } from "./MediaPicker";

export default function AdminMedia() {
  const [items, setItems] = useState({ items: [] });
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/media?limit=100").then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const onFiles = async (files) => {
    setError("");
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        if (!f.type.startsWith("image/")) {
          setError(`"${f.name}" bukan berkas gambar.`);
          continue;
        }
        if (f.size > 8 * 1024 * 1024) {
          setError(`"${f.name}" terlalu besar (maks. 8 MB).`);
          continue;
        }
        await uploadMedia(f);
      }
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveMeta = async () => {
    try {
      await api.put(`/admin/media/${selected.id}`, { alt: selected.alt, caption: selected.caption, credit: selected.credit });
      setNotice("Keterangan gambar tersimpan.");
      setTimeout(() => setNotice(""), 2000);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (m) => {
    try {
      const { data } = await api.delete(`/admin/media/${m.id}`);
      if (data.ok === false) {
        if (window.confirm(data.message + "\n\nHapus tetap? Gambar akan hilang dari tempat tersebut.")) {
          await api.delete(`/admin/media/${m.id}?force=true`);
          setNotice("Gambar dihapus.");
          setSelected(null);
          load();
        }
        return;
      }
      setNotice("Gambar dihapus.");
      setSelected(null);
      load();
    } catch (err) {
      if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-media-page">
      <h1 className="font-serif text-3xl text-ink">Pustaka Media</h1>

      <label
        data-testid="media-upload-zone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files); }}
        className="block border-2 border-dashed border-line rounded-xl p-10 text-center cursor-pointer hover:border-enji transition-colors mt-6"
      >
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
        {uploading ? (
          <span className="inline-flex items-center gap-2 text-sub text-sm"><Loader2 size={16} className="animate-spin" /> Mengunggah &amp; mengompres…</span>
        ) : (
          <span className="inline-flex items-center gap-2 text-sub text-sm"><UploadCloud size={18} /> Tarik foto ke sini atau klik untuk mengunggah (maks. 8 MB)</span>
        )}
      </label>
      {error && <p role="alert" className="text-sm text-enji mt-3">{error}</p>}
      {notice && <p data-testid="media-notice" className="text-sm text-moss mt-3">{notice}</p>}

      <p className="text-[13px] text-faint mt-4">Gambar otomatis diputar sesuai orientasi, diubah ke format WebP, dan disimpan permanen di database.</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
        <div className="lg:col-span-7 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {items.items.map((m) => (
            <button
              key={m.id}
              data-testid={`media-item-${m.id}`}
              onClick={() => setSelected({ ...m })}
              className={`rounded-md overflow-hidden border-2 transition-colors ${selected?.id === m.id ? "border-enji" : "border-line"}`}
              style={{ aspectRatio: "1" }}
            >
              <img src={mediaUrl(m.id)} alt={m.alt || m.filename} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
          {items.items.length === 0 && <p className="text-faint text-sm col-span-full">Belum ada gambar.</p>}
        </div>

        <div className="lg:col-span-5 bg-surface border border-line rounded-xl p-6" data-testid="media-detail">
          {selected ? (
            <>
              <img src={mediaUrl(selected.id)} alt={selected.alt || ""} className="w-full rounded-md border border-line" />
              <p className="text-[12px] text-faint mt-3">{selected.width}×{selected.height}px · {(selected.size / 1024).toFixed(0)} KB</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="field-label" htmlFor="media-alt">Alt text (deskripsi untuk aksesibilitas)</label>
                  <input id="media-alt" data-testid="media-alt-input" value={selected.alt || ""} onChange={(e) => setSelected({ ...selected, alt: e.target.value })} className="field-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="media-caption">Caption</label>
                  <input id="media-caption" data-testid="media-caption-input" value={selected.caption || ""} onChange={(e) => setSelected({ ...selected, caption: e.target.value })} className="field-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="media-credit">Kredit / sumber</label>
                  <input id="media-credit" data-testid="media-credit-input" value={selected.credit || ""} onChange={(e) => setSelected({ ...selected, credit: e.target.value })} className="field-input" />
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={saveMeta} data-testid="media-save-button" className="btn-primary !min-h-[40px] !px-5 text-sm">Simpan</button>
                  <button onClick={() => del(selected)} data-testid="media-delete-button" className="btn-ghost !min-h-[40px] !px-5 text-sm !text-enji">Hapus</button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-faint text-sm">Pilih gambar untuk mengatur alt text, caption, kredit, atau menghapus.</p>
          )}
        </div>
      </div>
    </div>
  );
}
